/* ==========================================================================
   wivi.js · v1.5 · 03/11/2026
   Il JS nostro sopra Avone. Cinque pezzi, ognuno autonomo:
   <wivi-lingua>  apre e chiude il pannello della lingua (il cambio lingua è un form: va anche senza JS)
   <wivi-chat>    il foulard che apre la chat di Shopify Inbox
   dedica         dice cosa fare quando Zepto ferma il carrello sulla dedica vuota
   anteprima      la foto del foulard non è mai un «badge» per Zepto: la dedica si vede sulla foto anche sul telefono
   <wivi-preventivo>  i controlli dei moduli delle pagine «Eventi e aziende» e «Contatti»
   ========================================================================== */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- lingua */
  class WiviLingua extends HTMLElement {
    connectedCallback() {
      if (this._pronto) return;
      this._pronto = true;
      this.apriBtn = this.querySelector('.wv-lingua__apri');
      this.pannello = this.querySelector('.wv-lingua__pannello');
      if (!this.apriBtn || !this.pannello) return;

      this.apriBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.aperto ? this.chiudi() : this.apri();
      });
      this._fuori = (e) => { if (this.aperto && !this.contains(e.target)) this.chiudi(); };
      document.addEventListener('click', this._fuori);
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.aperto) { this.chiudi(); this.apriBtn.focus(); }
      });
      this.addEventListener('focusout', (e) => {
        if (this.aperto && e.relatedTarget && !this.contains(e.relatedTarget)) this.chiudi();
      });
      // la lingua scelta gira mentre la pagina si ricarica nell'altra lingua
      this.pannello.addEventListener('click', (e) => {
        const voce = e.target.closest('.wv-lingua__voce');
        if (voce) voce.classList.add('is-invio');
      });
    }
    disconnectedCallback() { document.removeEventListener('click', this._fuori); }
    get aperto() { return this.apriBtn.getAttribute('aria-expanded') === 'true'; }
    apri() {
      document.querySelectorAll('wivi-lingua.is-aperto').forEach((l) => l !== this && l.chiudi());
      this.apriBtn.setAttribute('aria-expanded', 'true');
      this.classList.add('is-aperto');
    }
    chiudi() {
      this.apriBtn.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-aperto');
    }
  }
  if (!customElements.get('wivi-lingua')) customElements.define('wivi-lingua', WiviLingua);

  /* ------------------------------------------------------------------ chat */
  class WiviChat extends HTMLElement {
    connectedCallback() {
      if (this._pronto) return;
      this._pronto = true;
      this.btn = this.querySelector('.wv-chat__btn');
      this.etichetta = this.querySelector('.wv-chat__etichetta');
      this.misuraBarra();
      this.aggancia(0);
    }

    // Inbox carica il suo pulsante quando vuole: lo si cerca per una decina di secondi.
    // `part="activator"` è l'aggancio pubblico che Inbox espone per lo stile: il più stabile.
    aggancia(tentativo) {
      const host = document.querySelector('shopify-chat');
      const radice = host && host.shadowRoot;
      const attivatore = radice && (radice.querySelector('[part~="activator"]') || radice.querySelector('button'));
      if (!attivatore) {
        if (tentativo < 40) setTimeout(() => this.aggancia(tentativo + 1), 250);
        return;
      }
      this.radice = radice;
      this.attivatore = attivatore;

      const nome = attivatore.getAttribute('aria-label') || attivatore.textContent.trim();
      if (nome) {
        this.btn.setAttribute('aria-label', nome);
        this.etichetta.textContent = nome;
      }
      // il pulsante di Inbox resta cliccabile da qui, ma sparisce per occhi, tastiera e lettori
      attivatore.setAttribute('tabindex', '-1');
      attivatore.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.add('wv-chat-attiva');

      this.btn.addEventListener('click', () => { const x = this.trovaAttivatore(); if (x) x.click(); });
      this._osserva = new MutationObserver(() => this.sincronizza());
      this._osserva.observe(radice, { subtree: true, attributes: true, childList: true });
      this.sincronizza();

      this.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-pronta')));
      this.presentati();
    }

    // Misurato il 28/09/2026: al primo click Inbox sostituisce il suo pulsante con
    // <shopify-agent-runtime>, che ha un suo shadow DOM con dentro un altro pulsante
    // [part=activator] (esportato, quindi il nostro CSS lo nasconde lo stesso) e la
    // finestra in un iframe. Il pulsante giusto si cerca su tutti e due i livelli.
    trovaAttivatore() {
      const rt = this.radice.querySelector('shopify-agent-runtime');
      const sr = rt && rt.shadowRoot;
      return (sr && sr.querySelector('[part~="activator"]')) || this.radice.querySelector('[part~="activator"]');
    }

    // stato aperto/chiuso e messaggi non letti, copiati dal pulsante di Inbox
    sincronizza() {
      const rt = this.radice.querySelector('shopify-agent-runtime');
      if (rt && rt.shadowRoot && rt.shadowRoot !== this._radiceRt) {
        this._radiceRt = rt.shadowRoot;
        this._osserva.observe(rt.shadowRoot, { subtree: true, attributes: true, childList: true });
      }
      const a = this.trovaAttivatore();
      if (a && a !== this.attivatore) {
        this.attivatore = a;
        a.setAttribute('tabindex', '-1');
        a.setAttribute('aria-hidden', 'true');
      }
      const aperta = a ? a.getAttribute('aria-expanded') === 'true' : !!(rt && rt.hasAttribute('open'));
      this.classList.toggle('is-chat-aperta', aperta);
      this.btn.setAttribute('aria-expanded', String(aperta));
      const ind = (a && a.querySelector('.activator__indicator')) || this.radice.querySelector('.activator__indicator');
      let nonLetti = false;
      if (ind && !ind.hidden) {
        const s = getComputedStyle(ind);
        nonLetti = s.display !== 'none' && s.visibility !== 'hidden' && ind.getBoundingClientRect().width > 0;
      }
      this.classList.toggle('ha-messaggi', nonLetti);
    }

    // una volta per visita, l'etichetta scivola fuori da sola per farsi notare
    presentati() {
      let giaVisto = false;
      try { giaVisto = sessionStorage.getItem('wv-chat-visto') === '1'; } catch (e) { /* storage non disponibile */ }
      if (giaVisto) return;
      setTimeout(() => {
        this.classList.add('mostra-etichetta');
        setTimeout(() => this.classList.remove('mostra-etichetta'), 3800);
        try { sessionStorage.setItem('wv-chat-visto', '1'); } catch (e) { /* storage non disponibile */ }
      }, 1800);
    }

    // sul telefono il foulard sta sopra la barra fissa di Avone (menu, cerca, carrello)
    misuraBarra() {
      const barra = document.querySelector('.mbtlwraper');
      const radice = document.documentElement;
      const misura = () => {
        let h = 0;
        if (barra) {
          const r = barra.getBoundingClientRect();
          if (r.height > 0 && r.top < window.innerHeight && getComputedStyle(barra).display !== 'none') {
            h = Math.max(0, window.innerHeight - r.top);
          }
        }
        radice.style.setProperty('--wv-barra', h + 'px');
      };
      misura();
      window.addEventListener('resize', misura, { passive: true });
      if (barra && 'ResizeObserver' in window) new ResizeObserver(misura).observe(barra);
    }
  }
  if (!customElements.get('wivi-chat')) customElements.define('wivi-chat', WiviChat);

  /* ---------------------------------------------------------------- dedica */
  // Zepto Product Personalizer, dal 05/10/2026: «Personalizza il prodotto» parte acceso e, da
  // acceso, la dedica è obbligatoria. Se manca, Zepto ferma il carrello e mette `pplr_red_wrapper`
  // sul campo, ma non dice niente: qui compare cosa fare. Si segue la classe di Zepto, non si rifà
  // il suo controllo. Misurato: la mette al clic (anche coi soli spazi), la toglie alla prima
  // lettera, e spegnendo l'interruttore il campo sparisce dalla pagina insieme all'avviso.
  const DEDICA = 'input[name="properties[La tua dedica]"]';
  const inglese = /^en\b/i.test(document.documentElement.lang);

  const avvisaDedica = (campo) => {
    const etichetta = campo.closest('.product-personalizer').querySelector('.pplr-switch .pplrlabel');
    const nome = (etichetta && etichetta.textContent.trim()) || 'Personalizza il prodotto';
    const avviso = document.createElement('p');
    avviso.className = 'wv-dedica-avviso';
    avviso.id = 'wv-dedica-avviso-' + (campo.dataset.frame || '0');
    avviso.setAttribute('role', 'alert');
    avviso.textContent = inglese
      ? `Write your dedication. If you don't want one, switch off “${nome}”.`
      : `Scrivi la tua dedica. Se non la vuoi, spegni «${nome}».`;
    (campo.closest('.pplr-field-box') || campo).after(avviso);
    campo.setAttribute('aria-describedby', avviso.id);
    // sul telefono il campo può essere fuori schermo, sopra il pulsante: lo si riporta in vista
    const r = campo.getBoundingClientRect();
    if (r.top < 90 || r.bottom > window.innerHeight - 90) campo.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const seguiDedica = (form) => {
    const aggiorna = () => form.querySelectorAll('.product-personalizer .pplr-wrapper').forEach((w) => {
      const campo = w.querySelector(DEDICA);
      const avviso = w.querySelector('.wv-dedica-avviso');
      const fermo = !!campo && w.classList.contains('pplr_red_wrapper');
      if (fermo && !avviso) avvisaDedica(campo);
      else if (!fermo && avviso) {
        avviso.remove();
        if (campo) campo.removeAttribute('aria-describedby');
      }
    });
    // Zepto si monta nel form quando vuole: si osserva il form, non il personalizzatore
    new MutationObserver(aggiorna).observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  };
  document.querySelectorAll('form[action$="/cart/add"]').forEach(seguiDedica);

  /* ------------------------------------------------------------- anteprima */
  // Zepto sceglie da solo la foto su cui disegnare la dedica, e scarta come «badge di sconto» ogni
  // immagine con «%» (o «sale», «sold out») nell'alt più stretta di 400 px (`pplr_is_overlay_img`).
  // L'alt delle foto è il titolo, «… 100% Seta …», e sul telefono la foto è larga 360 px: veniva
  // scartata e l'anteprima finiva in una sezione più in basso (misurato il 03/11/2026, 72 su 72).
  // Qui la foto della galleria del prodotto non è mai un badge; per tutto il resto decide Zepto.
  // Il «100%» nell'alt resta: lo vuole Rami (03/11/2026).
  const GALLERIA = '.product-image-lg';
  const proteggiAnteprima = () => {
    const originale = window.pplr_is_overlay_img;
    if (typeof originale !== 'function' || originale._wivi) return false;
    const nostra = function (img) {
      try { if (img && img.closest && img.closest(GALLERIA)) return false; } catch (e) { /* non è un elemento */ }
      return originale.apply(this, arguments);
    };
    nostra._wivi = true;
    window.pplr_is_overlay_img = nostra;
    return true;
  };
  // lo script di Zepto arriva quando vuole, da un altro dominio: si aspetta la funzione e la si avvolge
  // appena c'è, prima che scelga la foto (misurato: la sceglie almeno mezzo secondo dopo averla definita)
  if (document.querySelector('.product-personalizer, form[action$="/cart/add"]') && !proteggiAnteprima()) {
    const inizio = Date.now();
    const attesa = setInterval(() => { if (proteggiAnteprima() || Date.now() - inizio > 30000) clearInterval(attesa); }, 10);
  }

  /* ------------------------------------------------------------ preventivo */
  // I moduli delle pagine «Eventi e aziende» e «Contatti» (sections/wivi-b2b-form.liquid). Mentre si scrive
  // filtra wivi-campi.js (kit-moduli, window.CAMPI); qui il controllo all'uscita dal campo e
  // all'invio, coi messaggi che dicono cosa fare. Senza JS restano i `required` del browser.
  const PREVENTIVO = {
    it: {
      nome: 'Scrivi il tuo nome e cognome.', email: 'Scrivi la tua email, così possiamo risponderti.',
      tipo: 'Scegli il tipo di evento.', body: 'Raccontaci in due righe che cosa ti serve.', body_contatto: 'Scrivi il tuo messaggio.',
      fmt_nome: 'Il nome può contenere solo lettere.', fmt_email: "Controlla l'email: dovrebbe essere come nome@dominio.it.",
      fmt_telefono: 'Il telefono ha da 6 a 15 cifre.', fmt_data: "La data dell'evento è già passata.",
      invia: 'Controlla i campi segnati in rosso.'
    },
    en: {
      nome: 'Please write your full name.', email: 'Please write your email, so we can reply.',
      tipo: 'Please choose the type of event.', body: 'Tell us in a couple of lines what you need.', body_contatto: 'Please write your message.',
      fmt_nome: 'The name can only contain letters.', fmt_email: 'Check your email: it should look like name@domain.com.',
      fmt_telefono: 'The phone number has 6 to 15 digits.', fmt_data: 'The event date has already passed.',
      invia: 'Please check the fields marked in red.'
    }
  };

  class WiviPreventivo extends HTMLElement {
    connectedCallback() {
      if (this._pronto) return;
      this._pronto = true;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.testi = PREVENTIVO[this.dataset.lingua] || PREVENTIVO.it;
      this.avviso = this.querySelector('.wv-preventivo__avviso');
      this.form.noValidate = true;

      // la data dell'evento parte da oggi
      const data = this.form.querySelector('[data-tipo="data"]');
      if (data) {
        const oggi = new Date();
        data.min = new Date(oggi.getTime() - oggi.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      }

      this.form.addEventListener('input', (e) => {
        const el = e.target;
        if (el.hasAttribute('data-wv-numero')) el.value = el.value.replace(/\D/g, '').slice(0, 6);
        // numero d'ordine di Shopify: «#» solo all'inizio, poi cifre (#1001)
        if (el.hasAttribute('data-wv-ordine')) el.value = el.value.replace(/[^\d#]/g, '').replace(/(?!^)#/g, '').slice(0, 12);
        if (el.getAttribute('aria-invalid') === 'true') this.controlla(el);
      });
      this.form.addEventListener('change', (e) => { if (e.target.tagName === 'SELECT' || e.target.type === 'date') this.controlla(e.target); });
      this.form.addEventListener('focusout', (e) => { if (e.target.matches('input, select, textarea')) this.controlla(e.target); });

      this.form.addEventListener('submit', (e) => {
        const campi = [...this.form.querySelectorAll('input:not([type="hidden"]), select, textarea')];
        const sbagliati = campi.filter((el) => !this.controlla(el));
        if (!sbagliati.length) {
          if (this.avviso) this.avviso.hidden = true;
          return;
        }
        e.preventDefault();
        if (this.avviso) { this.avviso.textContent = this.testi.invia; this.avviso.hidden = false; }
        sbagliati[0].focus({ preventScroll: true });
        sbagliati[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
      });

      // dopo l'invio Shopify ricarica la pagina con il messaggio di conferma: lo si porta in vista
      const ok = this.querySelector('.wv-preventivo__ok');
      if (ok) requestAnimationFrame(() => { ok.scrollIntoView({ block: 'center' }); ok.focus({ preventScroll: true }); });
    }

    // true se il campo va bene; altrimenti scrive sotto cosa fare
    controlla(el) {
      const valore = (el.value || '').trim();
      const tipo = el.dataset.tipo;
      let msg = '';
      if (!valore) {
        if (el.required) msg = this.testi[el.dataset.chiave] || this.testi.invia;
      } else if (tipo === 'data') {
        if (el.min && valore < el.min) msg = this.testi.fmt_data;
      } else if (tipo && window.CAMPI) {
        if (window.CAMPI.errore(tipo, valore)) msg = this.testi['fmt_' + tipo] || '';
      }
      const err = el.id && this.querySelector('#' + el.id + '-err');
      if (err) { err.textContent = msg; err.hidden = !msg; }
      if (msg) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid');
      return !msg;
    }
  }
  if (!customElements.get('wivi-preventivo')) customElements.define('wivi-preventivo', WiviPreventivo);
})();
